import { cloneDeep, omit } from "lodash";
import { PingQueryParameterType } from "./setQueryParameter";
import { ShapeFilter } from "../ts-types/mapsTypes";
import { PING_YEAR_BUILT_BANDS_MAP } from "../components/Constants";
import { calculateBoundingBox, getBoundingPolygon } from "./maps-utils";

export const getSearchParameters = ({
  sovid,
  lat1,
  lng1,
  lat2,
  lng2,
  filters,
  attach,
  layerLimit,
  shapeFilter,
}: {
  sovid: string;
  lat1?: number;
  lng1?: number;
  lat2?: number;
  lng2?: number;
  filters?: Record<string, PingQueryParameterType>;
  attach?: number;
  layerLimit?: number;
  shapeFilter?: ShapeFilter | null;
}) => {
  let localFilters: Record<string, PingQueryParameterType> =
    cloneDeep(filters) || {};
  const searchParameters = new URLSearchParams();

  searchParameters.set("sovid", sovid);

  if (shapeFilter) {
    const bbox = calculateBoundingBox(shapeFilter);
    const boundingPolygon = getBoundingPolygon(shapeFilter);

    if (bbox) {
      searchParameters.set("lat1", bbox.lat1.toString());
      searchParameters.set("lng1", bbox.lng1.toString());
      searchParameters.set("lat2", bbox.lat2.toString());
      searchParameters.set("lng2", bbox.lng2.toString());
    }
    if (boundingPolygon) {
      searchParameters.set("bounding_polygon", boundingPolygon);
    }
  } else if ([lat1, lng1, lat2, lng2].every(Number.isFinite)) {
    searchParameters.set("lat1", String(lat1));
    searchParameters.set("lng1", String(lng1));
    searchParameters.set("lat2", String(lat2));
    searchParameters.set("lng2", String(lng2));
  }

  if (localFilters && Object.keys(localFilters).includes("year_built_band")) {
    let ybbFilters: Record<string, string> = {};
    if (Array.isArray(localFilters["year_built_band"])) {
      for (const f of localFilters["year_built_band"]) {
        if (typeof f === "string" && f in PING_YEAR_BUILT_BANDS_MAP) {
          const bandFilters =
            PING_YEAR_BUILT_BANDS_MAP[
              f as keyof typeof PING_YEAR_BUILT_BANDS_MAP
            ];
          // Convert number values to strings
          const convertedFilters = Object.fromEntries(
            Object.entries(bandFilters).map(([key, value]) => [
              key,
              value.toString(),
            ]),
          );
          ybbFilters = { ...ybbFilters, ...convertedFilters };
        }
      }
    } else {
      const bandValue = localFilters["year_built_band"];
      if (
        typeof bandValue === "string" &&
        bandValue in PING_YEAR_BUILT_BANDS_MAP
      ) {
        const bandFilters =
          PING_YEAR_BUILT_BANDS_MAP[
            bandValue as keyof typeof PING_YEAR_BUILT_BANDS_MAP
          ];
        // Convert number values to strings
        ybbFilters = Object.fromEntries(
          Object.entries(bandFilters).map(([key, value]) => [
            key,
            value.toString(),
          ]),
        );
      }
    }

    localFilters = omit(localFilters, ["year_built_band"]);
    localFilters = { ...localFilters, ...ybbFilters };
  }

  for (const [key, value] of Object.entries(localFilters)) {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParameters.append(key, v));
    } else if (value !== null && value !== undefined) {
      searchParameters.append(key, value.toString());
    }
  }

  if (
    attach !== undefined &&
    layerLimit !== undefined &&
    attach !== null &&
    layerLimit !== null
  ) {
    searchParameters.set("attach", attach.toString());
    searchParameters.set("layer_limit", layerLimit.toString());
    searchParameters.set("limits__total_limit__gt", attach.toString());
  }

  return searchParameters;
};
